import React from "react";
import { Container, RadioButtonWithLabel } from "../../../components";
import { Text, Button } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { BankIdRefererType } from "../../../../../constants";
import { AnnouncementType, RootState } from "../../../../../models";
import { BusinessDetailsDetails } from "../../../../../models/state/business";
import { RejectReasonID } from "../../../../../models/transfer";
import { getOrderItemGuid, isPortin } from "../../../app/state/selectors";
import { isBankIdVerified as isBankIdVerifiedSelector } from "../../../authentication/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import OrgOrNameInput from "../../../checkout/components/molecules/OrgOrNameInput/OrgOrNameInput";
import { checkOrgName, checkOrgNumber } from "../../../checkout/state/actions/businessActions";
import {
    bankIdResetWhenConsentToB2B,
    updateFormerLegalB2B,
} from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getExistingVerifiedCompanyAdmin,
    getSelectedPhoneNumber,
    getTransferExistingIndividualLegalOwnerFullName,
    getTransferExistingOrganizationName,
    getTransferNewIndividualLegalOwnerFullName,
    getTransferNewLegalOwnerField,
} from "../../../checkout/state/selectors/accountSelectors";
import {
    getBrregNames,
    getOrganizationName,
    getOrganizationNumber,
} from "../../../checkout/state/selectors/businessDetailsSelectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import TEXT from "../../text-consts";

type AcceptType = "accept-b2b" | "accept-b2c";

interface AcceptContentProps {
    isB2BTransfer: boolean;
    setRejectReasons: React.Dispatch<React.SetStateAction<RejectReasonID | null>>;
    setSubSelectedOption: React.Dispatch<React.SetStateAction<AcceptType | null>>;
    subSelectedOption: string | null;
}

const AcceptContent: React.FC<AcceptContentProps> = ({
    isB2BTransfer,
    subSelectedOption,
    setSubSelectedOption,
    setRejectReasons,
}) => {
    const dispatch = useDispatch();
    const newLegalOrganizationName = useSelector((state: RootState) =>
        getTransferNewLegalOwnerField(state, "organization", "name"),
    );
    const newLegalOwnerIndividualName = useSelector((state: RootState) =>
        getTransferNewIndividualLegalOwnerFullName(state),
    );
    const selectedPhoneNumber = useSelector((state: RootState) => getSelectedPhoneNumber(state));
    const isBankIdVerified = useSelector((state: RootState) => isBankIdVerifiedSelector(state));
    const existingLegalOwner = useSelector(
        (state: RootState) => getTransferExistingIndividualLegalOwnerFullName(state) || "",
    );

    const verifiedCompanyAdmin = useSelector((state: RootState) => getExistingVerifiedCompanyAdmin(state));
    const formerLegalOrgName = useSelector(
        (state: RootState) => getOrganizationName(state) || getTransferExistingOrganizationName(state) || "",
    );
    const isPortinOrder = useSelector((state: RootState) => isPortin(state));

    const bankIdOptions = {
        referer: "consentFormerLegal" as BankIdRefererType,
        typeId: "consent_former_legal" as AnnouncementType,
        skipDisplayHeading: true,
        skipDisplayError: true,
        skipDisplayDescription: true,
    };

    const shouldShowBankId =
        !isBankIdVerified &&
        (subSelectedOption === "accept-b2c" || (subSelectedOption === "accept-b2b" && formerLegalOrgName));

    const nameOfPersonFromBankId = existingLegalOwner || verifiedCompanyAdmin;

    return (
        <div className="consent-landing-page__consent-expanded">
            {!isBankIdVerified && (
                <section>
                    <div
                        className="paragraph"
                        dangerouslySetInnerHTML={{
                            __html: TEXT.CONSENT_FORMER_LEGAL.IDENTIFICATION_TEXT(
                                isB2BTransfer ? newLegalOrganizationName : newLegalOwnerIndividualName,
                                getFormattedPhoneNumber(selectedPhoneNumber),
                            ),
                        }}
                        data-di-mask
                    />
                </section>
            )}
            {isBankIdVerified && !!nameOfPersonFromBankId && (
                <>
                    <Text mb="100" variant="additional-100">
                        {TEXT.CONSENT_FORMER_LEGAL.BANK_ID_VERIFIED}
                    </Text>
                    <div className="consent-landing-page__consent-expanded bankid-information" data-di-mask>
                        <Text mb="100" variant="additional-100" className="telia-sans-medium">
                            {nameOfPersonFromBankId}
                        </Text>
                    </div>
                </>
            )}
            <div className="consent-landing-page__consent-option-b2b">
                {(isPortinOrder || (formerLegalOrgName && isB2BTransfer)) && (
                    <SubSelectionOptionB2B
                        checked={subSelectedOption === "accept-b2b"}
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as AcceptType);
                            setRejectReasons(null);
                        }}
                        isBankIdVerified={isBankIdVerified}
                    />
                )}
                {!isBankIdVerified && (isPortinOrder || (isB2BTransfer && !formerLegalOrgName)) && (
                    <RadioButtonWithLabel
                        value="accept-b2c"
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as AcceptType);
                            setRejectReasons(null);
                        }}
                        name="tlo-accept-b2b"
                        checked={subSelectedOption === "accept-b2c"}
                        label="Jeg godkjenner som privatperson"
                    />
                )}
                {isBankIdVerified && (subSelectedOption === "accept-b2b" || subSelectedOption === "accept-b2c") && (
                    <div className="consent-landing-page__start-over-container">
                        <Text mb="100">{TEXT.CONSENT_FORMER_LEGAL.START_OVER(subSelectedOption === "accept-b2b")}</Text>
                        <div className="consent-landing-page__button-wrapper">
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={() => {
                                    setSubSelectedOption(null);
                                    dispatch(bankIdResetWhenConsentToB2B());
                                }}
                            >
                                Start på nytt
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            {shouldShowBankId && (
                <Container className="consent-landing-page__bankid-container">
                    <BankIdSection {...bankIdOptions} />
                </Container>
            )}
        </div>
    );
};

export default AcceptContent;

const SubSelectionOptionB2B = ({
    checked,
    onChange,
    isBankIdVerified,
}: {
    checked: boolean;
    isBankIdVerified: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
    const brRegOrganizationNumber = useSelector((state: RootState) => getOrganizationNumber(state) || "");
    const brregNameSearch = useSelector((state: RootState) => getBrregNames(state));
    const orderItemGuid = useSelector((state: RootState) => getOrderItemGuid(state));
    const brRegOrganizationName = useSelector((state: RootState) => getOrganizationName(state) || "");

    const dispatch = useDispatch();
    if (isBankIdVerified) return null;

    return (
        <>
            <RadioButtonWithLabel
                value="accept-b2b"
                onChange={onChange}
                name="tlo-accept-b2b"
                checked={checked}
                label="Jeg godkjenner på vegne av en bedrift"
            />
            {checked && (
                <div className="consent-landing-page__subaccept-company">
                    {(!isBankIdVerified || (isBankIdVerified && !brRegOrganizationName)) && (
                        <OrgOrNameInput
                            checkOrgName={(name) => dispatch(checkOrgName(name))}
                            checkOrgNumber={async (orgNumber) => {
                                const org: Partial<BusinessDetailsDetails> = await dispatch(checkOrgNumber(orgNumber));

                                if (org) {
                                    const orgName = org.businessName || "";
                                    const organizationNumber = org.orgNumber || "";
                                    if (orgName !== "" && organizationNumber !== "") {
                                        await dispatch(
                                            updateFormerLegalB2B(orderItemGuid, orgName, organizationNumber),
                                        );
                                    }
                                }
                            }}
                            businesses={brregNameSearch}
                            data-di-mask
                        />
                    )}
                    {brRegOrganizationName && (
                        <div className="consent-landing-page__selected-company">
                            Valgt organisasjon: <span className="telia-sans-medium">{brRegOrganizationName}</span>{" "}
                            (org.nr.: {brRegOrganizationNumber})
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
